import { useState, useEffect, useCallback } from "react";

export type InstallPlatform =
  | "chrome-android"
  | "chrome-desktop"
  | "edge"
  | "safari-ios"
  | "safari-desktop"
  | "firefox"
  | "other";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

function detectPlatform(): InstallPlatform {
  const ua = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua)) return "safari-ios";
  if (/edg/.test(ua)) return "edge";
  if (/firefox/.test(ua)) return "firefox";
  if (/chrome/.test(ua)) {
    return /mobile|android/.test(ua) ? "chrome-android" : "chrome-desktop";
  }
  if (/safari/.test(ua)) {
    return /mobile/.test(ua) ? "safari-ios" : "safari-desktop";
  }
  return "other";
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [platform] = useState<InstallPlatform>(detectPlatform);

  useEffect(() => {
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as unknown as { standalone?: boolean }).standalone === true;
    setIsStandalone(standalone);
    setIsInstalled(standalone);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener("beforeinstallprompt", handler);

    const onAppInstalled = () => {
      setDeferredPrompt(null);
      setIsInstalled(true);
    };
    window.addEventListener("appinstalled", onAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", onAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (isInstalled) {
      return { success: false, platform: "installed" as const };
    }

    // Android Chrome 可以主动弹安装对话框
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === "accepted") {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
      return { success: outcome === "accepted", platform: "chrome-android" as const };
    }

    // 其他平台返回对应类型，由 UI 显示引导提示
    return { success: false, platform };
  }, [deferredPrompt, isInstalled, platform]);

  // 按钮是否可点击：已安装时不让点，其他情况都可以点（会显示引导提示）
  const canInstall = !isInstalled;

  return {
    canInstall,
    isInstalled,
    isStandalone,
    platform,
    promptInstall,
  };
}